import { z } from "zod";
import {
	projectMemberRoleSchema,
	projectTypeSchema,
	tagTypeSchema,
	timeInputModeSchema,
	WHO_CAN_ADD_TASKS_PM_ONLY,
	whoCanAddTasksSchema,
	workspaceUserRoleSchema,
} from "./enums.js";
import {
	apiClientColorSchema,
	apiDescriptionSchema,
	apiEntityNameSchema,
	apiProjectColorSchema,
	apiTimeNameSchema,
	apiUuidFormatSchema,
	apiWorkspaceNameSchema,
} from "./fields.js";
import { PERMISSION_SETTINGS_KEYS } from "./permission-keys.js";

export const idRefSchema = z.object({
	id: apiUuidFormatSchema,
});

export const namedIdRefSchema = idRefSchema.extend({
	name: z.string().nullish(),
});

export const clientInputSchema = z.object({
	name: apiEntityNameSchema,
	description: apiDescriptionSchema.nullish(),
	color: apiClientColorSchema.nullish(),
	useAsDefault: z.boolean().default(false),
	meta: z.record(z.string(), z.unknown()).default({}),
	externalId: z.string().nullish(),
});

export const tagInputSchema = z.object({
	name: apiEntityNameSchema,
	color: apiProjectColorSchema.nullish(),
	tagType: tagTypeSchema,
	externalId: z.string().nullish(),
});

export const taskProjectInputSchema = namedIdRefSchema.extend({
	completed: z
		.boolean()
		.nullish()
		.transform((value) => value ?? false),
});

export const taskInputSchema = z.object({
	name: apiEntityNameSchema,
	project: taskProjectInputSchema,
	client: namedIdRefSchema.nullish(),
	assignedToUsers: z
		.array(namedIdRefSchema)
		.nullish()
		.transform((value) => value ?? []),
	assignedToGroups: z
		.array(namedIdRefSchema)
		.nullish()
		.transform((value) => value ?? []),
	estimation: z
		.number()
		.nullish()
		.transform((value) => value ?? 0),
	completed: z.boolean().default(false),
	externalId: z.string().nullish(),
});

export const projectMemberInputSchema = z.object({
	id: apiUuidFormatSchema,
	role: projectMemberRoleSchema,
	removed: z
		.boolean()
		.nullish()
		.transform((value) => value ?? false),
	billable_rate: z.number().nullable().optional(),
});

export const projectGroupInputSchema = z.object({
	id: apiUuidFormatSchema,
	role: projectMemberRoleSchema,
	removed: z
		.boolean()
		.nullish()
		.transform((value) => value ?? false),
});

export const projectInputSchema = z.object({
	name: apiEntityNameSchema,
	description: apiDescriptionSchema.nullish(),
	color: apiProjectColorSchema.nullish(),
	billable: z
		.boolean()
		.nullish()
		.transform((value) => value ?? false),
	private: z
		.boolean()
		.nullish()
		.transform((value) => value ?? false),
	whoCanAddTasks: whoCanAddTasksSchema.default(WHO_CAN_ADD_TASKS_PM_ONLY),
	projectType: projectTypeSchema.nullish().transform((value) => value ?? "project"),
	client: namedIdRefSchema,
	tags: z
		.array(namedIdRefSchema)
		.nullish()
		.transform((value) => value ?? []),
	users: z
		.array(projectMemberInputSchema)
		.nullish()
		.transform((value) => value ?? []),
	groups: z
		.array(projectGroupInputSchema)
		.nullish()
		.transform((value) => value ?? []),
	taskBasedEstimation: z.boolean().default(false),
	estimation: z
		.number()
		.nullish()
		.transform((value) => value ?? 0),
	externalId: z
		.string()
		.normalize("NFKC")
		.nullish()
		.transform((value) => value?.trim()),
	templateId: z.string().optional(),
	templateCopyRate: z.boolean().optional(),
	templateCopyTasks: z.boolean().optional(),
	templateCopyTaskAssign: z.boolean().optional(),
});

export const timeProjectInputSchema = namedIdRefSchema.extend({
	color: z.string().nullable().optional(),
	completed: z.boolean().optional(),
	useAsDefault: z.boolean().optional(),
	tags: z
		.array(namedIdRefSchema)
		.nullish()
		.transform((value) => value ?? []),
});

export const timeTaskInputSchema = namedIdRefSchema.extend({
	completed: z.boolean().optional(),
});

export const timeInputSchema = z.object({
	name: apiTimeNameSchema.nullish(),
	billable: z.boolean().default(false),
	start: z.coerce.date<string | Date>(),
	end: z.coerce.date<string | Date>().nullish(),
	project: timeProjectInputSchema.nullish(),
	task: timeTaskInputSchema.nullish(),
	client: namedIdRefSchema.nullish(),
	tags: z
		.array(namedIdRefSchema)
		.nullish()
		.transform((value) => value ?? []),
	timeZone: z.string().default("UTC"),
	user: namedIdRefSchema.nullish(),
	inputMode: timeInputModeSchema.nullish().transform((value) => value ?? "range"),
	recordType: z.string().optional(),
	outlookCalenderReference: z.string().nullish(),
});

export const groupInputSchema = z.object({
	name: apiEntityNameSchema,
	users: z.array(namedIdRefSchema).default([]),
	synchronizedGroup: z.boolean().default(false),
});

export const workspaceSettingsLeavesSchema = z.object({
	requireName: z.boolean().default(false),
	requireProject: z.boolean().default(false),
	requireTask: z.boolean().default(false),
	workinghoursEnabled: z.boolean().default(false),
	projecthoursEnabled: z.boolean().default(false),
	allHoursEnabled: z.boolean().default(false),
	isNewProjectBillable: z.boolean().default(true),
	showLogoPdfExports: z.boolean().default(false),
});

const permissionSettingsKeySchema = z.enum(PERMISSION_SETTINGS_KEYS);

export const permissionSettingsInputSchema = z.record(permissionSettingsKeySchema, z.array(idRefSchema));

export const workspaceCreateUserInputSchema = z.looseObject({
	id: apiUuidFormatSchema,
	email: z.string().optional(),
	name: z.string().optional(),
	admin: z.boolean().optional(),
	role: workspaceUserRoleSchema.optional(),
});

/** JSON body for POST /add/workspaces. Persist defaults stay on the API. */
export const workspaceCreateInputSchema = z.looseObject({
	name: apiWorkspaceNameSchema,
	description: apiDescriptionSchema.optional().default(""),
	users: z.array(workspaceCreateUserInputSchema).default([]),
});

export type ClientInput = z.input<typeof clientInputSchema>;
export type TagInput = z.input<typeof tagInputSchema>;
export type TaskInput = z.input<typeof taskInputSchema>;
export type ProjectInput = z.input<typeof projectInputSchema>;
export type TimeInput = z.input<typeof timeInputSchema>;
export type GroupInput = z.input<typeof groupInputSchema>;
export type WorkspaceSettingsLeaves = z.input<typeof workspaceSettingsLeavesSchema>;
export type PermissionSettingsInput = z.input<typeof permissionSettingsInputSchema>;
export type WorkspaceCreateInput = z.input<typeof workspaceCreateInputSchema>;
export type WorkspaceCreateUserInput = z.input<typeof workspaceCreateUserInputSchema>;
